import React, { useState, useEffect, useRef } from 'react';
import { City } from '../types';
import { useLocalization } from '../localization/LocalizationContext';

interface CityAutocompleteProps {
  cities: City[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  onSelect: (city: City) => void;
  selectedCity: City | null;
  error?: string | null;
  onPopularCitySelect?: (cityName: string) => void;
}

// Popular cities data
const popularCities = [
  { name: 'Київ' },
  { name: 'Дніпро' },
  { name: 'Одеса' },
  { name: 'Харків' },
  { name: 'Львів' }
];

const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  cities,
  isLoading,
  onSearch,
  onSelect,
  selectedCity,
  error,
  onPopularCitySelect,
}) => {
  const { t } = useLocalization();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Log props changes
  useEffect(() => {
    console.log('CityAutocomplete - cities updated:', cities);
  }, [cities]);

  useEffect(() => {
    console.log('CityAutocomplete - isLoading updated:', isLoading);
  }, [isLoading]);

  useEffect(() => {
    console.log('CityAutocomplete - selectedCity updated:', selectedCity);
  }, [selectedCity]);

  useEffect(() => {
    console.log('CityAutocomplete - error updated:', error);
  }, [error]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        console.log('CityAutocomplete - clicked outside, closing dropdown');
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update input value when selectedCity changes
  useEffect(() => {
    if (selectedCity) {
      console.log('CityAutocomplete - updating query from selectedCity:', selectedCity.Description);
      setQuery(selectedCity.Description);
    }
  }, [selectedCity]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('CityAutocomplete - input changed:', value);
    setQuery(value);
    onSearch(value);
    setIsOpen(true);
  };

  const handleCitySelect = (city: City) => {
    console.log('CityAutocomplete - city selected:', city);
    onSelect(city);
    setIsOpen(false);
  };

  const handlePopularCityClick = (cityName: string) => {
    console.log('CityAutocomplete - popular city clicked:', cityName);
    setQuery(cityName);
    onSearch(cityName);

    // If onPopularCitySelect is provided, call it
    if (onPopularCitySelect) {
      onPopularCitySelect(cityName);
    }
  };

  // Debug function to check if cities array is valid
  const debugCities = () => {
    console.log('CityAutocomplete - debugging cities array:');
    console.log('Cities array length:', cities.length);
    console.log('Cities array is array?', Array.isArray(cities));
    if (cities.length > 0) {
      console.log('First city:', cities[0]);
    }
    return null;
  };

  return (
    <div className="mb-4 relative dropdown-container" ref={dropdownRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        </div>
        <input
          type="text"
          className={`shadow-sm pl-10 appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-custom-pink focus:border-custom-pink ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={t.cityStep.placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            console.log('CityAutocomplete - input focused, opening dropdown');
            setIsOpen(true);
          }}
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}

      {/* Popular Cities Section */}
      <div className="mt-3">
        <p className="text-sm text-gray-600 mb-2">{t.cityStep.popularCities}:</p>
        <div className="flex flex-wrap gap-2">
          {popularCities.map((city) => (
            <button
              key={city.name}
              type="button"
              onClick={() => handlePopularCityClick(city.name)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors duration-200"
            >
              {city.name}
            </button>
          ))}
        </div>
      </div>

      {isOpen && (isLoading || (cities && cities.length > 0) || (query.length > 0)) && (
        <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md max-h-60 overflow-auto border border-gray-200">
          {debugCities()}
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 flex items-center justify-center">
              <svg className="animate-spin mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t.cityStep.loading}
            </div>
          ) : cities && cities.length > 0 ? (
            <ul className="py-1">
              {cities.map((city, index) => (
                <li
                  key={city.Ref || `city-${index}`}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors duration-150"
                  onClick={() => handleCitySelect(city)}
                >
                  <div className="font-medium text-gray-800">
                    {city.Description || 'Unknown City'}
                  </div>
                  {city.Area && (
                    <div className="text-sm text-gray-500">
                      {city.Area}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : query.length > 0 ? (
            <div className="p-4 text-center text-gray-500">
              <div className="flex flex-col items-center">
                <svg className="h-10 w-10 text-gray-400 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p>{t.cityStep.noResults}</p>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;